import { useState } from "react";
import { useGetList } from "react-admin";
import permissions from "app/permissions";
import { getPermissions } from "auth/security/securityUtils";
import { administrationRequests } from "api/administrationApi";
import { employeeRequests } from "api/employeeApi";
import { studentRequests } from "api/studentApi";
import { checkPermission } from "server/security/utils";
import { getProfessorRecordText } from "server/components/Fields/_fieldsHelper";
import { CourseChoices } from "utils/choices";
import { ALL_OPTIONS } from "features/filters/constants";
import useSearchChoices from "features/filters/useSearchChoices";
import useSemesterFilter from "features/filters/useSemesterFilter";

// Drafts of the four weekly-schedule preview tabs, each { fields, isComplete, query } for <CustomFilter>.
// Every tab starts with year -> semester; changing those clears the tab's own fields.

const withoutEmpty = draft =>
  Object.fromEntries(Object.entries(draft).filter(([, value]) => value != null && value !== ""));

export const useRoomsFilter = () => {
  const [buildingId, setBuildingId] = useState(null);
  const [roomId, setRoomId] = useState(null);

  const semester = useSemesterFilter({
    sessionField: "sessionType",
    onChange: () => {
      setBuildingId(null);
      setRoomId(null);
    },
  });
  const buildings = useSearchChoices({
    request: administrationRequests.getBuildingsSelect(),
    enabled: semester.isComplete,
  });
  const rooms = useSearchChoices({
    request: administrationRequests.getRoomsSelect(),
    filter: { buildingId },
    enabled: Boolean(buildingId),
  });

  const changeBuilding = value => {
    setBuildingId(value);
    setRoomId(null);
  };

  const fields = [
    ...semester.fields,
    ...(semester.isComplete
      ? [{ label: "Сграда", type: "autocomplete", value: buildingId, setValue: changeBuilding, ...buildings }]
      : []),
    ...(buildingId
      ? [{ label: "Зала", type: "autocomplete", value: roomId, setValue: setRoomId, optionText: "roomName", ...rooms }]
      : []),
  ];

  return {
    fields,
    isComplete: Boolean(semester.isComplete && roomId),
    query: withoutEmpty({ ...semester.query, roomId }),
  };
};

export const useProfessorsFilter = () => {
  // Without this permission the backend returns the current professor's own schedule.
  const canSeeAll = checkPermission(getPermissions(), permissions.canSeeAllProfessorsSchedule);

  const [structuralUnitId, setStructuralUnitId] = useState(null);
  const [departmentId, setDepartmentId] = useState(null);
  const [employeeId, setEmployeeId] = useState(null);

  const semester = useSemesterFilter({
    sessionField: "sessionType",
    onChange: () => {
      setStructuralUnitId(null);
      setDepartmentId(null);
      setEmployeeId(null);
    },
  });
  const { data: structuralUnits } = useGetList(administrationRequests.getStructuralUnitsSelect(), ALL_OPTIONS, {
    enabled: Boolean(canSeeAll && semester.isComplete),
  });
  const departments = useSearchChoices({
    request: administrationRequests.getDepartmentsSelect(),
    filter: { structuralUnitId },
    enabled: Boolean(canSeeAll && structuralUnitId),
  });
  const professors = useSearchChoices({
    request: employeeRequests.getProfessorsSelect(),
    filter: { departmentId },
    enabled: Boolean(canSeeAll && departmentId),
  });

  const changeStructuralUnit = value => {
    setStructuralUnitId(value);
    setDepartmentId(null);
    setEmployeeId(null);
  };
  const changeDepartment = value => {
    setDepartmentId(value);
    setEmployeeId(null);
  };

  const fields = [
    ...semester.fields,
    ...(canSeeAll && semester.isComplete
      ? [
          {
            label: "Структурна единица",
            type: "select",
            value: structuralUnitId ?? "",
            setValue: changeStructuralUnit,
            choices: structuralUnits ?? [],
          },
        ]
      : []),
    ...(canSeeAll && structuralUnitId
      ? [{ label: "Катедра", type: "autocomplete", value: departmentId, setValue: changeDepartment, ...departments }]
      : []),
    ...(canSeeAll && departmentId
      ? [
          {
            label: "Преподавател",
            type: "autocomplete",
            value: employeeId,
            setValue: setEmployeeId,
            optionText: getProfessorRecordText,
            ...professors,
          },
        ]
      : []),
  ];

  return {
    fields,
    isComplete: Boolean(semester.isComplete && (!canSeeAll || employeeId)),
    query: withoutEmpty({ ...semester.query, employeeId }),
  };
};

export const useStudentFilter = () => {
  const [specialityId, setSpecialityId] = useState(null);
  const [studentEducationId, setStudentEducationId] = useState(null);

  const semester = useSemesterFilter({
    sessionField: "sessionType",
    onChange: () => {
      setSpecialityId(null);
      setStudentEducationId(null);
    },
  });
  const specialities = useSearchChoices({
    request: administrationRequests.getSpecialitiesSelect(),
    filter: { filterByUser: true },
    enabled: semester.isComplete,
  });
  const facultyNumbers = useSearchChoices({
    request: studentRequests.getFacultyNumbersSelect(),
    filter: { specialityId },
    searchKey: "number",
    enabled: Boolean(specialityId),
  });

  const changeSpeciality = value => {
    setSpecialityId(value);
    setStudentEducationId(null);
  };

  const fields = [
    ...semester.fields,
    ...(semester.isComplete
      ? [
          {
            label: "Специалност",
            type: "autocomplete",
            value: specialityId,
            setValue: changeSpeciality,
            ...specialities,
          },
        ]
      : []),
    ...(specialityId
      ? [
          {
            label: "Факултетен номер",
            type: "autocomplete",
            value: studentEducationId,
            setValue: setStudentEducationId,
            ...facultyNumbers,
          },
        ]
      : []),
  ];

  return {
    fields,
    isComplete: Boolean(semester.isComplete && studentEducationId),
    query: withoutEmpty({ ...semester.query, studentEducationId }),
  };
};

export const useStudyGroupsFilter = () => {
  const [curriculumId, setCurriculumId] = useState(null);
  const [instructionLanguageId, setInstructionLanguageId] = useState(null);
  const [course, setCourse] = useState(null);
  const [studyGroup, setStudyGroup] = useState("");

  const semester = useSemesterFilter({
    sessionField: "sessionType",
    onChange: () => {
      setCurriculumId(null);
      setInstructionLanguageId(null);
      setCourse(null);
      setStudyGroup("");
    },
  });
  const curriculums = useSearchChoices({
    request: studentRequests.getCurriculumsSelect(),
    enabled: semester.isComplete,
  });
  const { data: languages } = useGetList(
    administrationRequests.getInstructionLanguagesSelect(),
    { ...ALL_OPTIONS, filter: { curriculumId } },
    { enabled: Boolean(curriculumId) },
  );

  const changeCurriculum = value => {
    setCurriculumId(value);
    setInstructionLanguageId(null);
    setCourse(null);
    setStudyGroup("");
  };
  const changeLanguage = value => {
    setInstructionLanguageId(value);
    setCourse(null);
    setStudyGroup("");
  };
  const changeCourse = value => {
    setCourse(value);
    setStudyGroup("");
  };

  const fields = [
    ...semester.fields,
    ...(semester.isComplete
      ? [
          {
            label: "Учебен план",
            type: "autocomplete",
            value: curriculumId,
            setValue: changeCurriculum,
            ...curriculums,
          },
        ]
      : []),
    ...(curriculumId
      ? [
          {
            label: "Език на обучение",
            type: "select",
            value: instructionLanguageId ?? "",
            setValue: changeLanguage,
            choices: languages ?? [],
          },
        ]
      : []),
    ...(instructionLanguageId
      ? [{ label: "Курс", type: "select", value: course ?? "", setValue: changeCourse, choices: CourseChoices }]
      : []),
    // Optional; a group below 1 doesn't exist.
    ...(course
      ? [
          {
            label: "Учебна група",
            type: "number",
            value: studyGroup,
            setValue: value => setStudyGroup(value === "" || Number(value) >= 1 ? value : "1"),
          },
        ]
      : []),
  ];

  return {
    fields,
    isComplete: Boolean(semester.isComplete && curriculumId && instructionLanguageId && course),
    query: withoutEmpty({
      ...semester.query,
      curriculumId,
      instructionLanguageId,
      course,
      studyGroup: studyGroup === "" ? null : Number(studyGroup),
    }),
  };
};
